import { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { saveLease } from '@/data';
import { getLongNumberStr, convertPseudoDateToRealDate } from '@/helpers/common';
import { calculateLeaseLength, calculateNewLeaseDates } from '@/helpers/leaseHelpers';
import { ComparablesSection } from '@/components/BuildingTabs/PricingTool/ComparablesSection';
import { PricingWizardContextValue, DEFAULT_PRICING_INPUT } from '../types';

interface PricingBatchItemProps {
  unit: Unit;
  units: Unit[];
  lease: Lease | undefined;
  allLeases: Lease[];
  leaseMap: Map<string, Lease>;
  wizard: PricingWizardContextValue;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export function PricingBatchItem({
  unit,
  units,
  lease,
  allLeases,
  leaseMap,
  wizard,
  isExpanded,
  onToggleExpand,
}: PricingBatchItemProps) {
  const { state, actions } = wizard;
  const storedPricingData = state.unitPricingData.get(unit.id);
  const pricingData = useMemo(
    () =>
      storedPricingData || {
        ...DEFAULT_PRICING_INPUT,
        unitId: unit.id,
      },
    [storedPricingData, unit.id]
  );

  // Local state for form inputs - use string to allow empty values during editing
  const [rentIncreasePercent, setRentIncreasePercent] = useState<string | number>(
    pricingData.rentIncreasePercent
  );
  const [concessionMonths, setConcessionMonths] = useState<string | number>(
    pricingData.concessionMonths
  );
  const [leaseLengthMonths, setLeaseLengthMonths] = useState<string | number>(
    pricingData.leaseLengthMonths
  );
  const [saving, setSaving] = useState(false);

  // Helper to get numeric value (treats empty string as 0)
  const getNumericValue = (value: string | number): number => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  // Only sync from stored data when it actually changes (e.g., after save)
  useEffect(() => {
    if (storedPricingData) {
      setRentIncreasePercent(storedPricingData.rentIncreasePercent);
      setConcessionMonths(storedPricingData.concessionMonths);
      setLeaseLengthMonths(storedPricingData.leaseLengthMonths);
    }
  }, [storedPricingData]);

  // Helper to safely convert lease dates
  const getLeaseEndDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseEndDate as any);
  };

  const getLeaseStartDate = (lease: Lease): Date | null => {
    return convertPseudoDateToRealDate(lease.leaseStartDate as any);
  };

  // Calculate metrics
  const { currentMetrics, proposedMetrics } = useMemo(() => {
    if (!lease) return { currentMetrics: null, proposedMetrics: null };

    const startDate = getLeaseStartDate(lease);
    const endDate = getLeaseEndDate(lease);
    if (!startDate || !endDate) return { currentMetrics: null, proposedMetrics: null };

    const leaseLength = calculateLeaseLength(lease);
    const sqft = unit.sqft || 1;

    const totalConcessions =
      Math.round(
        (Array.isArray(lease.concessions)
          ? lease.concessions.reduce(
              (sum: number, c: any) => sum + (c.amount || 0) * lease.monthlyRent,
              0
            )
          : ((lease.concessions as number) || 0) * lease.monthlyRent) * 100
      ) / 100;

    const effectiveMonthlyRent =
      leaseLength > 0
        ? Math.round((lease.monthlyRent - totalConcessions / leaseLength) * 100) / 100
        : lease.monthlyRent;

    const currentMetrics = {
      monthlyRent: lease.monthlyRent,
      leaseLength,
      concessions: totalConcessions,
      effectiveMonthlyRent,
      effectiveAnnualRent: effectiveMonthlyRent * 12,
      effectiveAnnualRentPerSqft:
        Math.round(((effectiveMonthlyRent * 12) / sqft) * 100) / 100,
      annualRent: lease.monthlyRent * 12,
      annualRentPerSqft: (lease.monthlyRent * 12) / sqft,
      sqft,
    };

    // Calculate proposed using numeric values
    const increaseNum = getNumericValue(rentIncreasePercent);
    const concessionNum = getNumericValue(concessionMonths);
    const lengthNum = getNumericValue(leaseLengthMonths) || 12;

    const proposedMonthlyRent =
      currentMetrics.monthlyRent * (1 + increaseNum / 100);
    const proposedConcessionAmount = concessionNum * proposedMonthlyRent;
    const proposedEffectiveMonthlyRent =
      proposedMonthlyRent - proposedConcessionAmount / lengthNum;

    const proposedMetrics = {
      monthlyRent: Math.round(proposedMonthlyRent * 100) / 100,
      leaseLength: lengthNum,
      concessions: Math.round(proposedConcessionAmount * 100) / 100,
      effectiveMonthlyRent: Math.round(proposedEffectiveMonthlyRent * 100) / 100,
      effectiveAnnualRent: Math.round(proposedEffectiveMonthlyRent * 12 * 100) / 100,
      effectiveAnnualRentPerSqft:
        Math.round(((proposedEffectiveMonthlyRent * 12) / sqft) * 100) / 100,
      annualRent: Math.round(proposedMonthlyRent * 12 * 100) / 100,
      annualRentPerSqft: Math.round(((proposedMonthlyRent * 12) / sqft) * 100) / 100,
    };

    return { currentMetrics, proposedMetrics };
  }, [lease, unit, rentIncreasePercent, concessionMonths, leaseLengthMonths]);

  // Save draft lease
  const handleSave = async () => {
    if (!lease || !proposedMetrics) return;

    setSaving(true);
    try {
      const proposedConcessionsArray: any[] = [];
      if (proposedMetrics.concessions > 0) {
        const concMonths = proposedMetrics.concessions / proposedMetrics.monthlyRent;
        proposedConcessionsArray.push({
          month: 'draft',
          year: 'draft',
          amount: concMonths,
          type: 'rentMultiplier',
        });
      }

      const {startDate, endDate} = calculateNewLeaseDates(lease.leaseEndDate,{months:proposedMetrics.leaseLength});


      const draftLease: any = {
        ...lease,
        id: pricingData.draftLeaseId || `draft_${unit.id}_${Date.now()}`,
        status: 'draft',
        monthlyRent: proposedMetrics.monthlyRent,
        leaseStartDate: startDate,
        leaseEndDate: endDate,
        concessions: proposedConcessionsArray,
        totalConcessionAmount: proposedMetrics.concessions,
      };

      await saveLease(draftLease);

      actions.markUnitAsPriced(unit.id, draftLease.id);
      actions.updateUnitPricing(unit.id, {
        rentIncreasePercent: getNumericValue(rentIncreasePercent),
        concessionMonths: getNumericValue(concessionMonths),
        leaseLengthMonths: getNumericValue(leaseLengthMonths) || 12,
        isPriced: true,
        draftLeaseId: draftLease.id,
      });
    } catch (error) {
      console.error('Error saving draft lease:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!lease) {
    return (
      <div className="p-4 border border-zinc-700 rounded bg-zinc-800/70">
        <Typography variant="body2" color="textSecondary">
          {unit.name || unit.id} - No active lease
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={`border rounded transition-colors ${
        pricingData.isPriced
          ? 'border-green-600/50 bg-green-900/10'
          : 'border-zinc-700'
      }`}
    >
      {/* Compact row */}
      <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '16px', padding: '16px' }}>
        {/* Unit info */}
        <div style={{ minWidth: '80px', flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {unit.name || unit.id}
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
            {unit.numBedrooms}BR · {unit.sqft} sqft
          </Typography>
        </div>

        {/* Current rent */}
        <div style={{ minWidth: '90px', flexShrink: 0, textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary" display="block">
            Current
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ${getLongNumberStr(lease.monthlyRent)}
          </Typography>
        </div>

        {/* Inputs */}
        <TextField
          label="Increase %"
          type="number"
          value={rentIncreasePercent}
          onChange={(e) => setRentIncreasePercent(e.target.value)}
          size="small"
          sx={{ width: 100, flexShrink: 0 }}
          slotProps={{ htmlInput: { step: 0.5 } }}
        />
        <TextField
          label="Conc. (mo)"
          type="number"
          value={concessionMonths}
          onChange={(e) => setConcessionMonths(e.target.value)}
          size="small"
          sx={{ width: 100, flexShrink: 0 }}
          slotProps={{ htmlInput: { step: 0.5 } }}
        />
        <TextField
          label="Length (mo)"
          type="number"
          value={leaseLengthMonths}
          onChange={(e) => setLeaseLengthMonths(e.target.value)}
          size="small"
          sx={{ width: 100, flexShrink: 0 }}
        />

        {/* Proposed rent preview */}
        <div style={{ minWidth: '90px', flexShrink: 0, textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary" display="block">
            Proposed
          </Typography>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
            ${getLongNumberStr(proposedMetrics?.monthlyRent || 0)}
          </Typography>
        </div>

        {/* Status indicator */}
        <div style={{ minWidth: '32px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          {pricingData.isPriced && (
            <CheckIcon color="success" fontSize="small" />
          )}
        </div>

        {/* Action button */}
        <div style={{ minWidth: '80px', flexShrink: 0 }}>
          <Button
            variant={pricingData.isPriced ? 'outlined' : 'contained'}
            size="small"
            onClick={handleSave}
            disabled={saving}
            fullWidth
          >
            {saving ? 'Saving...' : pricingData.isPriced ? 'Update' : 'Save'}
          </Button>
        </div>

        {/* Expand button */}
        <IconButton onClick={onToggleExpand} size="small" sx={{ flexShrink: 0 }}>
          {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </div>

      {/* Expandable comparables section */}
      <Collapse in={isExpanded}>
        <div className="border-t border-zinc-700 p-4">
          {currentMetrics && proposedMetrics && (
            <ComparablesSection
              selectedUnit={unit}
              allUnits={units}
              leaseMap={leaseMap}
              allLeases={allLeases}
              proposedMetrics={proposedMetrics}
              currentMetrics={currentMetrics}
              showConcessionsAsMultiples={true}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}
